import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, updateUserProfile } from '../firebase';
import {
  enablePushNotifications,
  disablePushNotifications,
  getPermissionState,
  isPushSupported,
  getPushDiagnostics,
  triggerTestPush,
} from '../utils/notificationService';
import './PushNotificationSettings.css';

const NOTIFICATION_EVENTS = [
  {
    key: 'analysis_complete',
    label: 'Analysis complete',
    description: 'Get notified when video analysis finishes',
  },
  {
    key: 'generation_complete',
    label: 'Generation complete',
    description: 'Get notified when video generation finishes',
  },
];

function PushNotificationSettings({ user }) {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(getPermissionState());
  const [prefs, setPrefs] = useState({ enabled: false, events: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [diagOpen, setDiagOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    isPushSupported().then((ok) => { if (!cancelled) setSupported(ok); });
    return () => { cancelled = true; };
  }, []);

  // Tokens that exist on the user's Firestore doc. Tracked here (rather
  // than re-reading inside the diagnostics panel) so the count stays live
  // when the user opens/disables notifications from another tab.
  const [storedTokens, setStoredTokens] = useState([]);

  useEffect(() => {
    if (!user) { setLoading(false); return undefined; }
    const userRef = doc(db, 'users', user.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      const push = (data.notificationPreferences || {}).push || {};
      setPrefs({
        enabled: Boolean(push.enabled),
        events: Array.isArray(push.events) ? push.events : NOTIFICATION_EVENTS.map((e) => e.key),
      });
      setStoredTokens(Array.isArray(data.pushTokens) ? data.pushTokens : []);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [user]);

  // Pull a fresh diagnostics snapshot whenever the panel is opened, or
  // when the permission/prefs change while it's open (e.g. user just
  // flipped the toggle — they'll want to see updated SW + token state).
  useEffect(() => {
    if (!diagOpen) return undefined;
    let cancelled = false;
    setDiagLoading(true);
    getPushDiagnostics().then((result) => {
      if (cancelled) return;
      setDiag(result);
      setDiagLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setDiagLoading(false);
    });
    return () => { cancelled = true; };
  }, [diagOpen, permission, prefs.enabled]);

  const handleSendTest = async () => {
    if (testing) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const result = await triggerTestPush();
      setTestResult(result);
    } catch (e) {
      setTestError(e?.message || 'Could not send test notification.');
    } finally {
      setTesting(false);
    }
  };

  const savePrefs = async (next) => {
    setBusy(true);
    setError(null);
    setSavedAt(null);
    try {
      await updateUserProfile({
        notificationPreferences: {
          push: {
            enabled: Boolean(next.enabled),
            events: next.events,
          },
        },
      });
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || 'Could not save preferences.');
    } finally {
      setBusy(false);
    }
  };

  // The toggle should reflect whether push works on *this device*, not just
  // the account-wide Firestore flag. If Firestore says enabled but the
  // browser hasn't granted permission, the user won't receive anything here.
  const activeOnDevice = prefs.enabled && permission === 'granted';

  const handleMasterToggle = async () => {
    if (busy) return;
    if (activeOnDevice) {
      setBusy(true);
      setError(null);
      try {
        await disablePushNotifications();
        await savePrefs({ enabled: false, events: prefs.events });
      } catch (e) {
        setError(e.message || 'Could not disable notifications.');
      } finally {
        setBusy(false);
      }
    } else {
      setBusy(true);
      setError(null);
      const result = await enablePushNotifications();
      setPermission(getPermissionState());
      if (result.ok) {
        const events = prefs.events && prefs.events.length
          ? prefs.events
          : NOTIFICATION_EVENTS.map((e) => e.key);
        await savePrefs({ enabled: true, events });
      } else if (result.reason === 'denied') {
        setError('Permission was denied. Re-enable notifications in your browser settings.');
      } else if (result.reason === 'no-vapid') {
        setError('Push notifications are not configured for this environment.');
      } else if (result.reason === 'unsupported') {
        setSupported(false);
      } else {
        setError(`Could not enable notifications: ${result?.error?.message || (typeof result?.error === 'string' ? result.error : 'an unexpected error occurred')}. Please try again.`);
      }
      setBusy(false);
    }
  };

  const toggleEvent = async (key) => {
    const has = prefs.events.includes(key);
    const events = has ? prefs.events.filter((e) => e !== key) : [...prefs.events, key];
    await savePrefs({ enabled: prefs.enabled, events });
  };

  if (!supported) {
    return (
      <p className="settings-muted" data-testid="push-unsupported">
        Push notifications are not supported in this browser.
      </p>
    );
  }

  if (loading) {
    return <p className="settings-muted">Loading…</p>;
  }

  return (
    <div className="push-settings" data-testid="push-notification-settings">
      <p className="push-settings-desc">
        Receive browser notifications when your videos finish processing, even if you close this tab.
      </p>

      {permission === 'denied' && (
        <div className="push-settings-blocked">
          Notifications are blocked by your browser. Open your browser's site settings
          for this page and allow notifications to enable this feature.
        </div>
      )}

      {prefs.enabled && permission === 'default' && (
        <div className="push-settings-info" data-testid="push-device-setup">
          Notifications are enabled on your account but not set up on this browser.
          Flip the toggle to set them up here.
        </div>
      )}

      <div className="push-toggle-row push-toggle-master">
        <div className="push-toggle-label">
          <span className="push-toggle-title">Push notifications</span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={activeOnDevice}
          className={`push-toggle-switch ${activeOnDevice ? 'push-toggle-on' : ''}`}
          onClick={handleMasterToggle}
          disabled={busy || permission === 'denied'}
          data-testid="push-enable-btn"
        >
          <span className="push-toggle-knob" />
        </button>
      </div>

      {activeOnDevice && (
        <div className="push-event-list" data-testid="push-event-toggles">
          {NOTIFICATION_EVENTS.map((evt) => {
            const checked = prefs.events.includes(evt.key);
            return (
              <div key={evt.key} className="push-toggle-row push-toggle-event">
                <div className="push-toggle-label">
                  <span className="push-toggle-title">{evt.label}</span>
                  <span className="push-toggle-hint">{evt.description}</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={checked}
                  className={`push-toggle-switch ${checked ? 'push-toggle-on' : ''}`}
                  onClick={() => toggleEvent(evt.key)}
                  disabled={busy}
                  data-testid={`push-event-${evt.key}`}
                >
                  <span className="push-toggle-knob" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {error && <div className="settings-error" role="alert">{error}</div>}
      {savedAt && !error && <div className="settings-success" role="status">Saved.</div>}

      <DiagnosticsPanel
        open={diagOpen}
        onToggle={() => setDiagOpen((o) => !o)}
        loading={diagLoading}
        diag={diag}
        storedTokens={storedTokens}
        testing={testing}
        testError={testError}
        testResult={testResult}
        onSendTest={handleSendTest}
      />
    </div>
  );
}

// Compact label for the most common service worker states.
function describeSwState(state) {
  switch (state) {
    case 'active': return 'Active';
    case 'installing': return 'Installing…';
    case 'waiting': return 'Waiting to activate';
    case 'inactive': return 'Inactive';
    case 'not-registered': return 'Not registered';
    case 'error': return 'Error reading registration';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

function StatusDot({ tone }) {
  return <span className={`diag-dot diag-dot-${tone || 'neutral'}`} aria-hidden="true" />;
}

function DiagnosticsPanel({ open, onToggle, loading, diag, storedTokens, testing, testError, testResult, onSendTest }) {
  const tokenCount = storedTokens.length;
  const thisFp = diag?.tokenFingerprint || null;

  // For "this device's token is among the stored ones?" we compare the
  // server fingerprint embedded in test results (when available) — but we
  // can also infer presence by checking userAgent overlap. Simplest is to
  // show the user the per-token list and let them see for themselves.
  return (
    <div className="diag-section" data-testid="push-diagnostics">
      <button
        type="button"
        className="diag-toggle"
        onClick={onToggle}
        aria-expanded={open}
        data-testid="push-diag-toggle"
      >
        {open ? 'Hide diagnostics' : 'Show diagnostics'}
      </button>

      {open && (
        <div className="diag-body" data-testid="push-diag-body">
          {loading && !diag ? (
            <p className="settings-muted">Reading browser state…</p>
          ) : !diag ? (
            <p className="settings-muted">Diagnostics unavailable.</p>
          ) : (
            <dl className="diag-list">
              <div className="diag-row">
                <dt>Browser support</dt>
                <dd>
                  <StatusDot tone={diag.supported ? 'ok' : 'bad'} />
                  {diag.supported ? 'Push API available' : 'Not supported in this browser'}
                </dd>
              </div>
              <div className="diag-row">
                <dt>Notification permission</dt>
                <dd>
                  <StatusDot tone={diag.permission === 'granted' ? 'ok' : diag.permission === 'denied' ? 'bad' : 'warn'} />
                  {diag.permission}
                </dd>
              </div>
              <div className="diag-row">
                <dt>Service worker</dt>
                <dd>
                  <StatusDot tone={diag.serviceWorker === 'active' ? 'ok' : diag.serviceWorker === 'not-registered' ? 'bad' : 'warn'} />
                  {describeSwState(diag.serviceWorker)}
                </dd>
              </div>
              <div className="diag-row">
                <dt>VAPID key configured</dt>
                <dd>
                  <StatusDot tone={diag.vapidConfigured ? 'ok' : 'bad'} />
                  {diag.vapidConfigured ? 'Yes' : 'Missing — push cannot register'}
                </dd>
              </div>
              <div className="diag-row">
                <dt>This device's token</dt>
                <dd>
                  {thisFp ? (
                    <>
                      <StatusDot tone="ok" />
                      <code data-testid="push-diag-fingerprint">{thisFp}</code>
                    </>
                  ) : (
                    <>
                      <StatusDot tone="warn" />
                      <span>No active token on this device</span>
                    </>
                  )}
                </dd>
              </div>
              <div className="diag-row">
                <dt>Tokens on account</dt>
                <dd>
                  <StatusDot tone={tokenCount > 0 ? 'ok' : 'warn'} />
                  <span data-testid="push-diag-token-count">{tokenCount}</span>
                </dd>
              </div>
              <div className="diag-row diag-row-full">
                <dt>User agent</dt>
                <dd><span className="diag-ua">{diag.userAgent || '—'}</span></dd>
              </div>
            </dl>
          )}

          <div className="diag-test-actions">
            <button
              type="button"
              className="diag-test-btn"
              onClick={onSendTest}
              disabled={testing || tokenCount === 0}
              data-testid="push-diag-test-btn"
            >
              {testing ? 'Sending…' : 'Send test notification'}
            </button>
            {tokenCount === 0 && (
              <span className="diag-test-hint">Enable push notifications first so there's a token to send to.</span>
            )}
          </div>

          {testError && (
            <div className="settings-error" role="alert" data-testid="push-diag-test-error">{testError}</div>
          )}

          {testResult && (
            <div className="diag-test-result" data-testid="push-diag-test-result">
              <p className="diag-test-summary">
                Sent to {testResult.totalTokens} {testResult.totalTokens === 1 ? 'device' : 'devices'}:{' '}
                <span className="diag-ok-count">{testResult.successCount} delivered</span>
                {testResult.failureCount > 0 && (
                  <>, <span className="diag-bad-count">{testResult.failureCount} failed</span></>
                )}.
              </p>
              {Array.isArray(testResult.results) && testResult.results.length > 0 && (
                <ul className="diag-test-list">
                  {testResult.results.map((r) => (
                    <li key={r.fingerprint} className={r.success ? 'diag-test-row-ok' : 'diag-test-row-bad'}>
                      <StatusDot tone={r.success ? 'ok' : 'bad'} />
                      <code>{r.fingerprint}</code>
                      <span className="diag-ua">{r.userAgent || 'unknown device'}</span>
                      {!r.success && r.error && (
                        <span className="diag-test-err"> — {r.error}</span>
                      )}
                      {thisFp && r.fingerprint === thisFp && (
                        <span className="diag-this-device"> (this device)</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {testResult.successCount > 0 && (
                <p className="settings-muted diag-test-hint">
                  If you didn't see a banner, check macOS Notifications settings for your browser
                  and confirm Focus / Do Not Disturb is off.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PushNotificationSettings;
